import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const DiscoverUs = () => {
  return (
    <>
      <Helmet>
        <title>AgroHarvest | About Us</title>
        <meta
          name="description"
          content="AgroHarvest Machineries - Premium quality farm equipment at unbeatable prices. Family-owned since 2001."
        />
      </Helmet>
      <TheDiscoverUs>
        <div className="about-hero">
          <div className="hero-inner">
            <span className="hero-label">
              Hopkinsville, Kentucky — Est. 2001
            </span>
            <h1>
              AgroHarvest
              <br />
              Machineries
            </h1>
            <p className="hero-sub">
              Premium Equipment. Honest Prices. Family Business.
            </p>
          </div>
          <div className="hero-rule" />
        </div>

        <div className="about-container">
          <section className="intro-section">
            <div className="intro-left">
              <h2>Our Story</h2>
            </div>
            <div className="intro-right">
              <p>
                AgroHarvest Machineries was established in 2001 in Hopkinsville,
                Kentucky. What started as a small family operation has grown
                into a trusted name in agricultural equipment distribution. Our
                founder recognized a crucial gap in the market — farmers needed
                access to quality equipment at fair prices, without inflated
                dealer markups.
              </p>
              <p>
                Today, AgroHarvest stands as a premier agricultural equipment
                supplier, serving farmers across the region with the same
                commitment to integrity and value that defined our earliest
                days.
              </p>
            </div>
          </section>

          <div className="divider" />

          <section className="quality-section">
            <div className="quality-header">
              <span className="section-tag">Equipment Quality</span>
              <h2>As Good As Brand New — At a Fraction of the Cost</h2>
            </div>
            <div className="quality-body">
              <p className="quality-lead">
                We don't hide the truth. Our equipment is reconditioned, not
                always factory-fresh. But it performs identically to new
                machines — and in many cases, better.
              </p>
              <div className="quality-columns">
                <div className="quality-col">
                  <h4>Expertly Reconditioned</h4>
                  <p>
                    Every machine undergoes rigorous testing, repairs, and
                    reconditioning before it leaves our facility. What you
                    receive performs identically to new equipment.
                  </p>
                </div>
                <div className="quality-col">
                  <h4>Modified for Performance</h4>
                  <p>
                    Many of our machines feature custom modifications that
                    enhance performance beyond original specifications. Our
                    engineering team has tested and proven every upgrade.
                  </p>
                </div>
                <div className="quality-col">
                  <h4>Same Work. Less Money.</h4>
                  <p>
                    A modified AgroHarvest machine does the exact same job as a
                    brand-new dealer machine — but you save 30–50% on cost. That
                    difference goes back into your operation.
                  </p>
                </div>
              </div>
              <div className="price-compare">
                <div className="price-item price-old">
                  <span className="price-label">Brand New Dealer Machine</span>
                  <span className="price-value">$100,000+</span>
                </div>
                <div className="price-vs">vs.</div>
                <div className="price-item price-new">
                  <span className="price-label">
                    AgroHarvest Modified Machine
                  </span>
                  <span className="price-value">$50,000 – $70,000</span>
                </div>
              </div>
            </div>
          </section>

          <div className="divider" />

          <section className="policy-section">
            <div className="policy-left">
              <span className="section-tag">Sales Policy</span>
              <h2>How We Sell</h2>
            </div>
            <div className="policy-right">
              <div className="policy-block">
                <h4>Bulk Purchase Requirement</h4>
                <p>
                  AgroHarvest operates as a wholesale supplier, which allows us
                  to keep our prices highly competitive. We primarily sell to
                  certified distributors and customers purchasing at least the
                  minimum order quantity (MOQ) of machines in a single
                  transaction.
                </p>
              </div>
              <div className="policy-block">
                <h4>Certified Supplier Pricing</h4>
                <p>
                  Only certified agricultural suppliers, dealers, and equipment
                  resellers qualify for our exclusive discounted pricing.
                  Becoming a certified supplier unlocks wholesale rates and
                  single-unit purchase options. Contact our sales team with your
                  business credentials, resale certificate, and volume
                  commitment to apply.
                </p>
              </div>
              <p className="policy-note">
                This policy ensures we can continue offering the industry's best
                prices by focusing on volume sales and maintaining strong
                manufacturer relationships.
              </p>
            </div>
          </section>

          <div className="divider" />

          <section className="model-section">
            <span className="section-tag">Business Model</span>
            <h2>How We Make It Happen</h2>
            <div className="steps">
              <div className="step">
                <span className="step-num">01</span>
                <div className="step-content">
                  <h4>Bulk Acquisition</h4>
                  <p>
                    We purchase equipment in volume from multiple sources at
                    wholesale prices.
                  </p>
                </div>
              </div>
              <div className="step">
                <span className="step-num">02</span>
                <div className="step-content">
                  <h4>Expert Reconditioning</h4>
                  <p>
                    Every machine is completely inspected, repaired, and tested
                    before anything else.
                  </p>
                </div>
              </div>
              <div className="step">
                <span className="step-num">03</span>
                <div className="step-content">
                  <h4>Performance Modifications</h4>
                  <p>
                    We add reinforcements and upgrades where original designs
                    had weaknesses.
                  </p>
                </div>
              </div>
              <div className="step">
                <span className="step-num">04</span>
                <div className="step-content">
                  <h4>Direct to You</h4>
                  <p>
                    No dealership markups. No middlemen. Quality equipment
                    delivered to your farm.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <div className="divider" />

          <section className="modifications-section">
            <div className="mod-left">
              <span className="section-tag">Our Modifications</span>
              <h2>Better Than Original?</h2>
              <p>
                Our engineering team identifies common failure points in
                original equipment and reinforces them during reconditioning.
                The result: machines that often outlast and outperform their
                brand-new counterparts.
              </p>
            </div>
            <div className="mod-right">
              <div className="mod-item">
                <h4>Transmission Reinforcement</h4>
                <p>Upgraded components in high-stress areas</p>
              </div>
              <div className="mod-item">
                <h4>Hydraulic System Upgrades</h4>
                <p>Enhanced flow and pressure capabilities</p>
              </div>
              <div className="mod-item">
                <h4>Structural Reinforcements</h4>
                <p>Additional bracing at failure-prone points</p>
              </div>
              <div className="mod-item">
                <h4>Performance Tuning</h4>
                <p>Optimized settings for maximum efficiency</p>
              </div>
            </div>
          </section>

          <div className="divider" />

          <section className="family-section">
            <div className="family-left">
              <span className="section-tag">Who We Are</span>
              <h2>The Family Behind AgroHarvest</h2>
              <p>
                AgroHarvest Machineries is proudly family-owned and operated.
                Our commitment to customer satisfaction and community support
                has been the cornerstone of our success for over two decades.
              </p>
            </div>
            <div className="family-right">
              <div className="value-row">
                <span className="value-name">Integrity</span>
                <span className="value-desc">
                  We're honest about our equipment — no hidden surprises
                </span>
              </div>
              <div className="value-row">
                <span className="value-name">Excellence</span>
                <span className="value-desc">
                  We continuously improve our reconditioning process
                </span>
              </div>
              <div className="value-row">
                <span className="value-name">Community</span>
                <span className="value-desc">
                  We support local farming communities
                </span>
              </div>
              <div className="value-row">
                <span className="value-name">Value</span>
                <span className="value-desc">
                  Maximum performance for minimum investment
                </span>
              </div>
            </div>
          </section>

          <div className="divider" />

          <section className="partners-section">
            <span className="section-tag">Partners</span>
            <h2>Our Manufacturing Partners</h2>
            <p className="partners-intro">
              We source equipment from industry-leading manufacturers and
              recondition them to our exacting standards.
            </p>
            <div className="partners-list">
              {[
                "CNH Industrial",
                "Case IH",
                "New Holland",
                "Krone",
                "Hitachi",
                "JCB",
                "JLG",
                "Genie",
                "Caterpillar",
              ].map((name) => (
                <span className="partner-tag" key={name}>
                  {name}
                </span>
              ))}
            </div>
            <p className="partner-note">
              Additional manufacturers available upon request.
            </p>
          </section>

          <div className="cta-block">
            <div className="cta-inner">
              <h3>Ready to get more machine for your money?</h3>
              <p>
                Don't pay dealership prices for equipment that does the same
                job.
              </p>
              <Link to="/contact-us" className="cta-button">
                Request a Bulk Quote
              </Link>
            </div>
          </div>
        </div>
      </TheDiscoverUs>
    </>
  );
};

const TheDiscoverUs = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--second-color);
  font-family: "Georgia", "Times New Roman", serif;
  color: #1c1c1c;

  /* ── HERO ── */
  .about-hero {
    background: var(--primary-color);
    padding: 7rem 6rem 4rem;
    position: relative;

    .hero-inner {
      max-width: 900px;

      .hero-label {
        display: block;
        font-family: "Courier New", monospace;
        font-size: 0.75rem;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--second-color);
        margin-bottom: 2rem;
      }

      h1 {
        font-size: clamp(3rem, 8vw, 6.5rem);
        font-weight: 400;
        color: #f5f2ed;
        line-height: 1;
        letter-spacing: -0.02em;
        margin: 0 0 1.8rem;
      }

      .hero-sub {
        font-size: 1rem;
        color: var(--second-color);
        font-style: italic;
        letter-spacing: 0.03em;
        margin: 0;
      }
    }

    .hero-rule {
      width: 100%;
      height: 1px;
      background: var(--primary-color);
      margin-top: 4rem;
    }
  }

  /* ── MAIN CONTAINER ── */
  .about-container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 4rem 6rem;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: #d5d0c8;
    margin: 4rem 0;
  }

  /* ── SHARED SECTION LABEL ── */
  .section-tag {
    display: inline-block;
    font-family: "Courier New", monospace;
    font-size: 0.7rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #7a7265;
    margin-bottom: 1rem;
  }

  /* ── INTRO ── */
  .intro-section {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 4rem;
    padding-top: 4rem;

    .intro-left {
      h2 {
        font-size: 2rem;
        font-weight: 400;
        color: var(--primary-color);
        line-height: 1.2;
        margin: 0;
        position: sticky;
        top: 2rem;
      }
    }

    .intro-right {
      p {
        font-size: 1.05rem;
        line-height: 1.85;
        color: #3d3d3d;
        margin-bottom: 1.2rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  /* ── QUALITY ── */
  .quality-section {
    .quality-header {
      margin-bottom: 2.5rem;

      h2 {
        font-size: clamp(1.6rem, 3vw, 2.2rem);
        font-weight: 400;
        color: var(--primary-color);
        max-width: 620px;
        line-height: 1.25;
        margin: 0;
      }
    }

    .quality-lead {
      font-size: 1.1rem;
      line-height: 1.8;
      color: #3d3d3d;
      max-width: 680px;
      margin-bottom: 2.5rem;
      font-style: italic;
      border-left: 3px solid var(--primary-color);
      padding-left: 1.5rem;
    }

    .quality-columns {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2.5rem;
      margin-bottom: 3rem;

      .quality-col {
        h4 {
          font-size: 0.85rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-family: "Courier New", monospace;
          color: var(--primary-color);
          margin: 0 0 0.8rem;
          font-weight: normal;
        }
        p {
          font-size: 0.95rem;
          line-height: 1.75;
          color: #555;
          margin: 0;
        }
      }
    }

    .price-compare {
      display: flex;
      align-items: center;
      gap: 2rem;
      background: var(--primary-color);
      padding: 2rem 2.5rem;
      border-radius: 2px;

      .price-item {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;

        .price-label {
          font-family: "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--second-color);
        }

        .price-value {
          font-size: 1.6rem;
          font-weight: 400;
          color: #f5f2ed;
          letter-spacing: -0.01em;
        }

        &.price-old .price-value {
          color: #7a9970;
          text-decoration: line-through;
          opacity: 0.7;
        }
      }

      .price-vs {
        font-family: "Courier New", monospace;
        font-size: 0.8rem;
        color: var(--second-color);
        letter-spacing: 0.1em;
        padding: 0 1rem;
      }
    }
  }

  /* ── POLICY ── */
  .policy-section {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 4rem;

    .policy-left {
      h2 {
        font-size: 2rem;
        font-weight: 400;
        color: var(--primary-color);
        line-height: 1.2;
        margin: 0;
        position: sticky;
        top: 2rem;
      }
    }

    .policy-right {
      .policy-block {
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid #d5d0c8;

        &:last-of-type {
          border-bottom: none;
        }

        h4 {
          font-size: 0.82rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          font-family: "Courier New", monospace;
          color: var(--primary-color);
          margin: 0 0 0.7rem;
          font-weight: normal;
        }

        p {
          font-size: 1rem;
          line-height: 1.8;
          color: #3d3d3d;
          margin: 0;
        }
      }

      .policy-note {
        font-size: 0.88rem;
        color: #7a7265;
        font-style: italic;
        margin: 0;
        line-height: 1.6;
      }
    }
  }

  /* ── HOW WE WORK ── */
  .model-section {
    h2 {
      font-size: 2rem;
      font-weight: 400;
      color: var(--primary-color);
      margin: 0 0 2.5rem;
    }

    .steps {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0;
      border-left: 1px solid #d5d0c8;

      .step {
        padding: 0 2rem 0 2rem;
        border-right: 1px solid #d5d0c8;

        .step-num {
          display: block;
          font-family: "Courier New", monospace;
          font-size: 0.72rem;
          letter-spacing: 0.15em;
          color: #7a7265;
          margin-bottom: 1rem;
        }

        h4 {
          font-size: 1rem;
          font-weight: 400;
          color: var(--primary-color);
          margin: 0 0 0.6rem;
          line-height: 1.3;
        }

        p {
          font-size: 0.88rem;
          line-height: 1.7;
          color: #555;
          margin: 0;
        }
      }
    }
  }

  /* ── MODIFICATIONS ── */
  .modifications-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5rem;
    align-items: start;

    .mod-left {
      h2 {
        font-size: 2rem;
        font-weight: 400;
        color: var(--primary-color);
        margin: 0 0 1.2rem;
      }
      p {
        font-size: 0.95rem;
        line-height: 1.8;
        color: #3d3d3d;
        margin: 0;
      }
    }

    .mod-right {
      .mod-item {
        padding: 1.2rem 0;
        border-bottom: 1px solid #d5d0c8;

        &:first-child {
          padding-top: 0;
        }
        &:last-child {
          border-bottom: none;
        }

        h4 {
          font-size: 0.82rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-family: "Courier New", monospace;
          color: var(--primary-color);
          margin: 0 0 0.3rem;
          font-weight: normal;
        }

        p {
          font-size: 0.9rem;
          color: #666;
          margin: 0;
        }
      }
    }
  }

  /* ── FAMILY ── */
  .family-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 5rem;

    .family-left {
      h2 {
        font-size: 2rem;
        font-weight: 400;
        color: var(--primary-color);
        margin: 0 0 1.2rem;
      }
      p {
        font-size: 0.95rem;
        line-height: 1.8;
        color: #3d3d3d;
        margin: 0;
      }
    }

    .family-right {
      .value-row {
        display: flex;
        gap: 2rem;
        align-items: baseline;
        padding: 1rem 0;
        border-bottom: 1px solid #d5d0c8;

        &:first-child {
          padding-top: 0;
        }
        &:last-child {
          border-bottom: none;
        }

        .value-name {
          font-size: 0.8rem;
          font-family: "Courier New", monospace;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--primary-color);
          min-width: 90px;
        }

        .value-desc {
          font-size: 0.92rem;
          color: #555;
          line-height: 1.5;
        }
      }
    }
  }

  /* ── PARTNERS ── */
  .partners-section {
    h2 {
      font-size: 2rem;
      font-weight: 400;
      color: var(--primary-color);
      margin: 0 0 0.8rem;
    }

    .partners-intro {
      font-size: 0.95rem;
      color: #555;
      line-height: 1.7;
      margin-bottom: 2rem;
    }

    .partners-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.6rem;
      margin-bottom: 1rem;

      .partner-tag {
        font-family: "Courier New", monospace;
        font-size: 0.75rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--primary-color);
        border: 1px solid var(--primary-color);
        padding: 0.35rem 0.9rem;
        border-radius: 100px;
        transition: all 0.2s ease;

        &:hover {
          background: var(--primary-color);
          color: #f5f2ed;
        }
      }
    }

    .partner-note {
      font-size: 0.82rem;
      color: #7a7265;
      font-style: italic;
      margin: 0;
    }
  }

  /* ── CTA ── */
  .cta-block {
    background: var(--primary-color);
    padding: 4rem;
    margin-top: 5rem;

    .cta-inner {
      max-width: 600px;

      h3 {
        font-size: 2rem;
        font-weight: 400;
        color: #f5f2ed;
        margin: 0 0 0.8rem;
        line-height: 1.25;
      }

      p {
        font-size: 1rem;
        color: var(--second-color);
        margin: 0 0 2rem;
        font-style: italic;
      }

      .cta-button {
        display: inline-block;
        background: transparent;
        color: #f5f2ed;
        border: 1px solid #f5f2ed;
        padding: 0.75rem 2rem;
        font-family: "Courier New", monospace;
        font-size: 0.78rem;
        letter-spacing: 0.15em;
        text-transform: uppercase;
        text-decoration: none;
        transition: all 0.25s ease;

        &:hover {
          background: #f5f2ed;
          color: var(--primary-color);
        }
      }
    }
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .about-hero {
      padding: 5rem 3rem 3rem;
    }
    .about-container {
      padding: 0 3rem 5rem;
    }
    .intro-section,
    .policy-section {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    .modifications-section,
    .family-section {
      grid-template-columns: 1fr;
      gap: 3rem;
    }
    .model-section .steps {
      grid-template-columns: repeat(2, 1fr);
      .step:nth-child(2) {
        border-right: none;
      }
      .step:nth-child(3) {
        margin-top: 2rem;
      }
      .step:nth-child(4) {
        border-right: none;
        margin-top: 2rem;
      }
    }
  }

  @media (max-width: 768px) {
    .about-hero {
      padding: 4rem 2rem 2.5rem;
      h1 {
        font-size: 3rem;
      }
    }
    .about-container {
      padding: 0 2rem 4rem;
    }
    .quality-section .quality-columns {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .quality-section .price-compare {
      flex-direction: column;
      gap: 1rem;
      .price-vs {
        padding: 0;
        align-self: flex-start;
      }
    }
    .model-section .steps {
      grid-template-columns: 1fr;
      border-left: none;
      border-top: 1px solid #d5d0c8;

      .step {
        padding: 1.5rem 0;
        border-right: none;
        border-bottom: 1px solid #d5d0c8;
        &:last-child {
          border-bottom: none;
        }
        &:nth-child(3),
        &:nth-child(4) {
          margin-top: 0;
        }
      }
    }
    .cta-block {
      padding: 3rem 2rem;
    }
  }

  @media (max-width: 480px) {
    .about-hero {
      padding: 3rem 1.5rem 2rem;
    }
    .about-container {
      padding: 0 1.5rem 3rem;
    }
    .family-section .family-right .value-row {
      flex-direction: column;
      gap: 0.3rem;
    }
  }
`;

export default DiscoverUs;
